import * as React from 'react'
import { cn } from '@/lib/utils'

interface ImageWithSkeletonProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  skeletonClassName?: string
  wrapperClassName?: string
}

/**
 * Module-level session cache: tracks every URL that has successfully loaded
 * at least once during this browser session. This survives component remounts,
 * so images never flash the skeleton on route navigations (e.g. side-panel open/close).
 */
const loadedUrls = new Set<string>()

export function ImageWithSkeleton({
  className,
  skeletonClassName,
  wrapperClassName,
  onLoad,
  onError,
  ...props
}: ImageWithSkeletonProps) {
  // If we've loaded this URL before in this session, start already-loaded
  const [loaded, setLoaded] = React.useState(() =>
    typeof props.src === 'string' && props.src ? loadedUrls.has(props.src) : false,
  )
  const [errored, setErrored] = React.useState(false)

  const handleLoad = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      // Persist to session cache so future mounts skip the skeleton
      if (typeof props.src === 'string' && props.src) {
        loadedUrls.add(props.src)
      }
      setLoaded(true)
      onLoad?.(e)
    },
     
    [onLoad, props.src],
  )

  const handleError = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      setLoaded(true)
      setErrored(true)
      onError?.(e)
    },
    [onError],
  )

  // Only reset state when src actually changes to a different URL
  const lastSrc = React.useRef(props.src)
  React.useLayoutEffect(() => {
    if (lastSrc.current !== props.src) {
      const alreadyKnown = typeof props.src === 'string' && props.src ? loadedUrls.has(props.src) : false
      setLoaded(alreadyKnown)
      setErrored(false)
      lastSrc.current = props.src
    }
  }, [props.src])

  return (
    <div className={cn('relative overflow-hidden', wrapperClassName)}>
      {!loaded && (
        <div
          className={cn('absolute inset-0 bg-accent animate-pulse', skeletonClassName)}
          aria-hidden="true"
        />
      )}

      {!errored && (
        <img
          {...props}
          onLoad={handleLoad}
          onError={handleError}
          className={cn(
            'transition-opacity duration-300',
            loaded ? 'opacity-100' : 'opacity-0',
            className,
          )}
        />
      )}
    </div>
  )
}
